import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { AudioProvider } from './contexts/AudioContext';
import { AuthProvider } from './contexts/AuthContext';
import { RadioPlayer } from './pages/RadioPlayer';
import { LoginPage } from './pages/LoginPage';
import { AuthCallback } from './pages/AuthCallback';
import { DonationSuccess } from './pages/DonationSuccess';
import { AdminDashboard } from './pages/AdminDashboard';
import './App.css';

// Register Service Worker for PWA
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((registration) => {
        console.log('SW registered:', registration.scope);
      })
      .catch((error) => {
        console.log('SW registration failed:', error);
      });
  });
}

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<RadioPlayer />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/donate/success" element={<DonationSuccess />} />
      <Route path="/admin" element={<AdminDashboard />} />
    </Routes>
  );
}

function App() {
  // Remove Emergent badge
  useEffect(() => {
    const removeBadge = () => {
      // Find and remove any Emergent badge elements
      const selectors = [
        'a[href*="emergentagent.com"]',
        'div:has(> a[href*="emergentagent.com"])',
        '[class*="emergent"]',
        '[data-testid*="emergent"]'
      ];
      
      selectors.forEach(selector => {
        try {
          document.querySelectorAll(selector).forEach(el => {
            if (el && el.textContent?.toLowerCase().includes('emergent')) {
              el.style.display = 'none';
              el.remove();
            }
          });
        } catch (e) {}
      });

      // Also check for badge by content
      document.querySelectorAll('a, div').forEach(el => {
        if (el.textContent?.includes('Made with Emergent') || 
            el.textContent?.includes('emergentagent')) {
          el.style.display = 'none';
          el.remove();
        }
      });
    };

    // Run immediately
    removeBadge();
    
    // Run after a delay (badge might load late)
    const timeouts = [100, 500, 1000, 2000, 3000].map(ms => 
      setTimeout(removeBadge, ms)
    );

    // Also use MutationObserver to catch dynamically added badges
    const observer = new MutationObserver(removeBadge);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      timeouts.forEach(clearTimeout);
      observer.disconnect();
    };
  }, []);

  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AudioProvider>
            <div className="App noise-overlay">
              <AppRouter />
            </div>
          </AudioProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
